import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { BenefitLogo } from "./AdminBenefits";
import { brl } from "./PurchaseDialog";
import { Check, X } from "lucide-react";

const LABELS = { name: "Nome", category: "Categoria", address: "Endereço", description: "Descrição", how_to_use: "Como usar",
                 original_price: "Preço original", price: "Preço associado" };
const fmt = (k, v) => (k.includes("price") ? brl(v) : v || "—");

const Diff = ({ b }) => {
  const pc = b.pending_changes || {};
  const keys = Object.keys(pc).filter((k) => String(pc[k] ?? "") !== String(b[k] ?? ""));
  if (!keys.length) return b.pending_logo_url ? null : <p className="text-xs text-muted-foreground">Sem mudanças de texto.</p>;
  return (
    <div className="mt-2 space-y-1 text-xs" data-testid={`review-diff-${b.id}`}>
      {keys.map((k) => (
        <div key={k}><b>{LABELS[k] || k}:</b> <span className="line-through text-muted-foreground">{fmt(k, b[k])}</span> → <span className="text-emerald-500">{fmt(k, pc[k])}</span></div>
      ))}
    </div>
  );
};

const ReviewRow = ({ b, onReject, onApprove }) => (
  <div className="p-4 rounded-xl border border-amber-500/40 bg-amber-500/5" data-testid={`review-item-${b.id}`}>
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="flex gap-2 shrink-0">
        <BenefitLogo url={b.logo_url} name={b.name} className="h-14 w-14" />
        {b.pending_logo_url && <BenefitLogo url={b.pending_logo_url} name={b.name} className="h-14 w-14 ring-2 ring-emerald-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium flex flex-wrap items-center gap-2">
          {b.name} <Badge variant="outline">{b.category}</Badge>
          <Badge className="bg-amber-500/20 text-amber-500 border-0">{b.has_pending_changes ? "Edição para aprovar" : "Novo cadastro"}</Badge>
        </div>
        <div className="text-xs text-muted-foreground">Por {b.owner_name} · enviado em {b.submitted_at ? new Date(b.submitted_at).toLocaleString("pt-BR") : "—"}</div>
        {b.has_pending_changes ? <Diff b={b} /> : (
          <div className="text-xs mt-1 space-y-0.5">
            <div>{b.address}</div><div className="text-muted-foreground">{b.description}</div>
            <div>De {brl(b.original_price)} por <b className="gold-text">{brl(b.price)}</b></div>
          </div>
        )}
      </div>
      <div className="flex sm:flex-col gap-2 shrink-0">
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onApprove(b)} data-testid={`review-approve-${b.id}`}><Check className="h-3 w-3 mr-1" /> Aprovar</Button>
        <Button size="sm" variant="outline" className="text-rose-500" onClick={() => onReject(b)} data-testid={`review-reject-${b.id}`}><X className="h-3 w-3 mr-1" /> Rejeitar</Button>
      </div>
    </div>
  </div>
);

export const AdminPartnerReview = ({ onChanged }) => {
  const [list, setList] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const load = useCallback(() => {
    api.get("/admin/benefits").then((r) => setList(r.data.filter((b) => b.status === "pending" || b.has_pending_changes)))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (b, action, why = "") => {
    try {
      await api.post(`/admin/benefits/${b.id}/review`, { action, reason: why });
      toast.success(action === "approve" ? "Aprovado e publicado" : "Rejeitado");
      setRejecting(null); setReason(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="p-6" data-testid="admin-partner-review">
      <div className="font-display text-lg font-bold">Aprovações pendentes</div>
      <p className="text-xs text-muted-foreground mb-4">Cadastros e edições enviados por associados. Só aparecem na vitrine depois de aprovados.</p>
      {!list ? <p className="text-sm text-muted-foreground">Carregando...</p> : list.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="review-empty">Nenhum cadastro aguardando aprovação.</p>
      ) : <div className="space-y-3">{list.map((b) => <ReviewRow key={b.id} b={b} onApprove={(x) => review(x, "approve")} onReject={setRejecting} />)}</div>}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="review-reject-dialog">
          <DialogHeader><DialogTitle>Rejeitar {rejecting?.name}?</DialogTitle></DialogHeader>
          <Textarea rows={3} placeholder="Motivo (o associado verá esta mensagem)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Voltar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => review(rejecting, "reject", reason)} data-testid="review-reject-confirm">Rejeitar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
